import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import {
  equipmentTypeParametersByEquipmentTypeID,
  getEquipmentType,
  getParameter,
  listParameters,
} from "graphql/queries";
import { updateEquipmentTypeParameter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import EquipmentTypeParamatersTable from "components/equipmentTypes/parameters/EquipmentTypeParametersTable";
import EquipmentTypeParameterCreateForm from "./EquipmentTypeParameterCreateForm";
import EquipmentTypeParameterUpdateForm from "./EquipmentTypeParameterUpdateForm";

const EquipmentTypeParameters = ({
  equipmentTypeID,
  equipmentTypeName,
  visible,
  onClose,
}) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "equipment-type:create-param",
    "equipment-type:update-param",
    "equipment-type:delete-param",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [equipmentTypeParameters, setEquipmentTypeParameters] = useState([]);
  const [equipmentTypeParametersLoading, setEquipmentTypeParametersLoading] =
    useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentTypeParameterId: null,
    equipmentTypeParameterName: null,
  });
  const [modalEquipmentTypeParameter, setModalEquipmentTypeParameter] =
    useState({
      visible: false,
      equipmentTypeParameterId: null,
    });

  const columnsMapped = useMemo(
    () =>
      equipmentTypeParameters.map((item) => {
        return {
          ...item,
          parameterName: item.parameterNameStr || <Loader />,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      equipmentTypeParameterId: item.id,
                      equipmentTypeParameterName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipmentTypeParameters]
  );

  const onNext = () => {
    fetchEquipmentTypeParameters(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchEquipmentTypeParameters(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchEquipmentTypeParameters(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalEquipmentTypeParameter({
      visible: true,
      action,
      equipmentTypeParameterId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalEquipmentTypeParameter({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchEquipmentTypeParameters();
  };

  async function fetchParameterName(id) {
    try {
      const parameterData = await API.graphql(
        graphqlOperation(getParameter, {
          id,
        })
      );
      const parameterItem = parameterData.data.getParameter;
      return parameterItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchParameterName", err);
      return "";
    }
  }

  const getParameterNames = (list) => {
    list?.forEach(async (item) => {
      const parameterNameStr = await fetchParameterName(item.parameterID);

      setEquipmentTypeParameters((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          parameterNameStr,
        };

        return updatedList;
      });
    });
  };

  async function getCompanies(id) {
    const dataReturned = await API.graphql(
      graphqlOperation(getEquipmentType, { id })
    );
    const itemReturned = dataReturned.data.getEquipmentType;
    if (!itemReturned) {
      toast.error(strings.responses.errorFetch);
      return;
    }
    return itemReturned.companies || [];
  }

  async function fetchEquipmentTypeParameters(token, isNext = true, keywords) {
    try {
      setEquipmentTypeParametersLoading(true);
      const limitMapped = 10;
      let filterObjList = [];

      if (keywords) {
        const companies = await getCompanies(equipmentTypeID);
        const parametersData = await API.graphql(
          graphqlOperation(listParameters, {
            filter: {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              or: companies.map((c) => ({ companies: { contains: c } })),
              deletedAt: { attributeExists: false },
            },
          })
        );
        const parametersItems = parametersData.data.listParameters.items;

        filterObjList =
          parametersItems?.map((param) => ({
            parameterID: { eq: param.id },
          })) ?? [];
      }

      const equipmentTypeParametersData = await API.graphql(
        graphqlOperation(equipmentTypeParametersByEquipmentTypeID, {
          equipmentTypeID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    ...filterObjList,
                    {
                      parameterValue: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const equipmentTypeParametersItems =
        equipmentTypeParametersData.data
          .equipmentTypeParametersByEquipmentTypeID.items;
      setEquipmentTypeParameters(equipmentTypeParametersItems);
      getParameterNames(equipmentTypeParametersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            equipmentTypeParametersData.data
              .equipmentTypeParametersByEquipmentTypeID.nextToken,
        };
      });
      setEquipmentTypeParametersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setEquipmentTypeParametersLoading(false);
    } finally {
      setEquipmentTypeParametersLoading(false);
    }
  }

  async function equipmentTypeParameterDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateEquipmentTypeParameter, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateEquipmentTypeParameter.id;
      setEquipmentTypeParameters(
        equipmentTypeParameters.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        equipmentTypeParameterId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchEquipmentTypeParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalEquipmentTypeParameter.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {equipmentTypeName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addEquipmentTypeParameter}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {equipmentTypeParametersLoading ? (
                  <Loader />
                ) : (
                  <EquipmentTypeParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.equipmentTypeParameterName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    equipmentTypeParameterId: null,
                  })
                }
                onConfirm={() =>
                  equipmentTypeParameterDelete(
                    modalDetails.equipmentTypeParameterId
                  )
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalEquipmentTypeParameter.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalEquipmentTypeParameter.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalEquipmentTypeParameter.action === "create" ? (
                <>
                  {strings.modalEquipmentTypeParameter.addTitle} -{" "}
                  {equipmentTypeName}
                </>
              ) : (
                <>
                  {strings.modalEquipmentTypeParameter.updateTitle} -{" "}
                  {equipmentTypeName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalEquipmentTypeParameter.action === "create" ? (
                <EquipmentTypeParameterCreateForm
                  equipmentTypeID={equipmentTypeID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <EquipmentTypeParameterUpdateForm
                  equipmentTypeID={equipmentTypeID}
                  equipmentTypeParameterId={
                    modalEquipmentTypeParameter.equipmentTypeParameterId
                  }
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Parámetros de Tipo de Equipo",
    successDeleted: "Parámetro de Tipo de Equipo eliminado con éxito",
    actions: {
      addEquipmentTypeParameter: "Agregar Parámetro de Tipo de Equipo",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar parámetros de tipo de equipo",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el parámetro de tipo de equipo {name}?",
    },
    modalEquipmentTypeParameter: {
      addTitle: "Agregar Parámetro de Tipo de Equipo",
      updateTitle: "Actualizar Parámetro de Tipo de Equipo",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default EquipmentTypeParameters;
